
import AP from '../../config/api.config'

/**
 * HumanVerification —— 场景化人机验证组件（一期：register）
 *
 * 契约（业务方只感知三件事）：
 *   props  : scenario（register/sms_send/login_risk/sensitive_action，一期仅 register 服务端开启）
 *            phone / clientId（client_session_id，与注册链五绑定一致）
 *   emit   : required(bool) —— 服务端配置是否要求验证（false 时组件整体不渲染）
 *            verified(token) —— 一次验证通过，携一次性 hv_token（业务随请求体上送）
 *   method : reset() —— 业务收到 403 HV_REQUIRED 时调用（重新出题）
 *
 * 引擎由服务端裁决（challenge 响应的 engine 字段），本组件不传引擎参数；
 * 拼图答案（targetX）永不下发——服务端只给素材，位置对不对由 POST verify 裁决。
 *
 * 触摸实现（仓内铁律）：
 *   - touchstart 不 preventDefault（吞合成 click 的坑）；仅在拖拽中的 document touchmove
 *     （passive:false）里、且横向位移占优时 preventDefault
 *   - 轨道/画布 CSS touch-action: pan-y —— 纵向手势原生滚动页面，横向手势交给 JS，互不干扰
 *   - 拼图块用 div 背景图渲染（非 <img>，避开 draggable=true 被 TouchPan/浏览器拒绝的坑）
 */
export default {
  name: 'HumanVerification',
  props: {
    scenario: { type: String, default: 'register' },
    phone: { type: String, default: '' },
    clientId: { type: String, default: '' }
  },
  data () {
    return {
      required: false,
      engine: '',
      challengeId: '',
      expiresIn: 0,
      // slider 素材（pieceWidth/pieceHeight = 拼块外接盒，视觉二期渲染口径；旧 payload 无此二字段时回退 sliderWidth）
      width: 300, height: 150, sliderWidth: 52, pieceWidth: 52, pieceHeight: 52, pieceY: 0,
      canvasW: 0, // 画布实测显示宽（响应式；scale 由此派生——$refs 非响应式，直接进 computed 会缓存死值）
      bgImage: '', pieceImage: '',
      // text 引擎
      textImage: '', textCode: '',
      // 拖拽态
      dragging: false,
      dragX: 0,
      track: [],
      verified: false,
      hvToken: '',
      verifying: false,
      statusText: '',
      statusType: 'info'
    }
  },
  computed: {
    scale () {
      // 显示宽 = 容器实测宽（canvasW，data 响应式）；坐标换算：图片像素 × scale = CSS 像素
      return this.canvasW > 0 && this.width ? this.canvasW / this.width : 1
    },
    displayHeight () {
      return this.height * this.scale
    }
  },
  watch: {
    phone (nv, ov) {
      if (nv === ov) return
      // challenge 绑定手机号：换号即作废当前验证态；出题防抖 400ms（逐键取题既浪费频控配额，
      // 乱序响应还会让显示题与最新题错位）
      this.verified = false
      this.hvToken = ''
      this.$emit('verified', '')
      if (this.required) {
        clearTimeout(this._phoneDebounce)
        this._phoneDebounce = setTimeout(() => { this.fetchChallenge() }, 400)
      }
    },
    bgImage (v) {
      // 题面渲染后量一次画布宽（canvas 是 v-if 的，mounted 时未必存在）
      if (v) this.$nextTick(() => this._measure())
    }
  },
  mounted () {
    this._measure()
    window.addEventListener('resize', this._measure)
  },
  created () {
    this.init()
  },
  beforeDestroy () {
    this._detach()
    clearTimeout(this._phoneDebounce)
    window.removeEventListener('resize', this._measure)
  },
  methods: {
    _measure () {
      const el = this.$refs.canvas
      if (el) {
        // 量宽必须用布局宽 offsetWidth：getBoundingClientRect 返回 transform 后的视觉宽，
        // 在 q-dialog 入场 scale 过渡中量题（取题快于动画时）会把过渡态宽写进 canvasW →
        // scale/displayHeight/拼块全按错宽计算，动画结束后画布比例失真（图被压扁）。
        // offsetWidth 不受祖先 transform 影响，过渡中即最终布局宽。
        const w = el.offsetWidth
        if (w > 0) this.canvasW = w
      }
    },
    async init () {
      try {
        const res = await this.axios.get(AP.apiList.hvChallengeConfig, { params: { scenario: this.scenario } })
        this.required = !!(res.data && res.data.data && res.data.data.required)
      } catch (_) {
        this.required = true // 配置读不到按需要处理（宁可多验证，不静默绕过）
      }
      this.$emit('required', this.required)
      if (this.required) await this.fetchChallenge(true)
    },
    async fetchChallenge () {
      this._setStatus('', 'info') // 换题即清旧文案（失败自动换题后不留陈旧的「位置不正确」）
      this.verified = false
      this.hvToken = ''
      this.$emit('verified', '')
      this.engine = ''
      this.bgImage = ''
      this.pieceImage = ''
      this.textImage = ''
      this.textCode = ''
      this.dragX = 0
      this.track = []
      try {
        const res = await this.axios.get(AP.apiList.hvChallenge, {
          params: { scenario: this.scenario, phone: this.phone, client_session_id: this.clientId }
        })
        const d = res.data && res.data.data
        if (!d || !d.challengeId) throw new Error('bad challenge')
        this.challengeId = d.challengeId
        this.engine = d.engine
        this.expiresIn = d.expiresIn || 120
        if (d.engine === 'slider') {
          this.width = d.width; this.height = d.height; this.sliderWidth = d.sliderWidth
          this.pieceWidth = d.pieceWidth || d.sliderWidth || 52
          this.pieceHeight = d.pieceHeight || d.sliderWidth || 52
          this.pieceY = d.pieceY || 0
          this.bgImage = d.bgImage; this.pieceImage = d.pieceImage
        } else if (d.engine === 'text') {
          this.textImage = d.image
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || '验证加载失败，请点击「换一个」重试'
        this._setStatus(msg, 'error')
      }
    },
    // ===== 拖拽（mouse + touch 统一到 _move/_end）=====
    onPointerDown (e) {
      if (this.engine !== 'slider' || this.verified || this.verifying) return
      if (e.touches) {
        // touchstart：只记录，不 preventDefault（纵向手势留给页面原生滚动）
        const t = e.touches[0]
        this._startX = t.clientX
        this._startY = t.clientY
        this._isTouch = true
        document.addEventListener('touchmove', this._onTouchMove, { passive: false })
        document.addEventListener('touchend', this._onTouchEnd)
        document.addEventListener('touchcancel', this._onTouchEnd)
      } else {
        this._startX = e.clientX
        this._startY = e.clientY
        this._isTouch = false
        document.addEventListener('mousemove', this._onMouseMove)
        document.addEventListener('mouseup', this._onMouseUp)
      }
      this._t0 = Date.now()
      this._baseDragX = this.dragX
      this.dragging = true
      this.track = [{ x: this.dragX, y: 0, t: 0 }]
    },
    _onMouseMove (e) { this._move(e.clientX, e.clientY) },
    _onMouseUp () { this._end() },
    _onTouchMove (e) {
      const t = e.touches[0]
      const dx = t.clientX - this._startX
      const dy = t.clientY - this._startY
      // 横向占优才接管（preventDefault 只在此处、拖拽中发生；纵向手势保持页面滚动）
      if (Math.abs(dx) > Math.abs(dy) + 2 || this.track.length > 1) {
        if (e.cancelable) e.preventDefault()
        this._move(t.clientX, t.clientY)
      } else {
        this._cancelDrag() // 纵向意图 → 放弃拖拽，还手势给滚动
      }
    },
    _onTouchEnd () { this._end() },
    _move (clientX, clientY) {
      const sc = this.scale || 1
      const maxDrag = (this.width - this.pieceWidth) * sc
      const dx = clientX - this._startX
      const next = Math.max(0, Math.min(maxDrag, this._baseDragX + dx))
      const dy = clientY - this._startY
      this.dragX = next
      if (this.track.length < 600) {
        this.track.push({ x: Math.round(next), y: Math.round(dy), t: Date.now() - this._t0 })
      }
    },
    _end () {
      if (!this.dragging) return
      this._detach()
      this.dragging = false
      const sc = this.scale || 1
      const moved = this.dragX / sc
      if (moved >= 8 && this.track.length >= 3) {
        this.submitSlide()
      } else {
        this.dragX = 0 // 太短视作误触，滑回原位
        this.track = []
      }
    },
    _cancelDrag () {
      this._detach()
      this.dragging = false
      this.dragX = 0
      this.track = []
    },
    _detach () {
      document.removeEventListener('mousemove', this._onMouseMove)
      document.removeEventListener('mouseup', this._onMouseUp)
      document.removeEventListener('touchmove', this._onTouchMove)
      document.removeEventListener('touchend', this._onTouchEnd)
      document.removeEventListener('touchcancel', this._onTouchEnd)
    },
    async submitSlide () {
      if (this.verifying) return
      this.verifying = true
      this._setStatus('', 'info')
      try {
        const sc = this.scale || 1
        const res = await this.axios.post(AP.apiList.hvVerify, {
          challengeId: this.challengeId,
          scenario: this.scenario,
          phone: this.phone,
          client_session_id: this.clientId,
          slider: {
            finalX: Math.round(this.dragX / sc), // 图片像素坐标（服务端容差口径）
            duration: Date.now() - this._t0,
            track: this.track
          }
        })
        this._onVerifyOk(res)
      } catch (err) {
        this._onVerifyFail(err)
      } finally {
        this.verifying = false
      }
    },
    async verifyText () {
      if (this.verifying || !this.textCode) return
      this.verifying = true
      this._setStatus('', 'info')
      try {
        const res = await this.axios.post(AP.apiList.hvVerify, {
          challengeId: this.challengeId,
          scenario: this.scenario,
          phone: this.phone,
          client_session_id: this.clientId,
          text: { code: this.textCode }
        })
        this._onVerifyOk(res)
      } catch (err) {
        this._onVerifyFail(err)
      } finally {
        this.verifying = false
      }
    },
    _onVerifyOk (res) {
      const token = res.data && res.data.data && res.data.data.hv_token
      this.verified = true
      this.hvToken = token || ''
      this._setStatus('验证通过', 'success')
      this.$emit('verified', this.hvToken)
    },
    _onVerifyFail (err) {
      const r = err.response && err.response.data
      const msg = (r && r.message) || '验证失败，请重试'
      this._setStatus(msg, 'error')
      this.dragX = 0
      this.track = []
      // challenge 失败即焚（服务端语义）→ 换新题；429 限流不换（题未消费）
      if (!err.response || err.response.status !== 429) {
        setTimeout(() => { if (!this.verified) this.fetchChallenge(true) }, 600)
      }
    },
    /** 业务收到 403 HV_REQUIRED 时调用：重置并重新出题 */
    reset () {
      this.verified = false
      this.hvToken = ''
      this.$emit('verified', '')
      this.fetchChallenge()
    },
    _setStatus (text, type) {
      this.statusText = text
      this.statusType = type || 'info'
    }
  }
}
